import React, { useState, useEffect } from 'react';
import { postData } from '../api/restapi';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import { RT_STRATEGIES } from './RtQuickStart';
import ActiveDebugSessions from './ActiveDebugSessions';
import '../assets/RdQuickStart.css';
import '../assets/RtQuickStart.css';
import '../assets/DebugStrategies.css';

/**
 * RD collector presets — mirrors the COLLECTORS constant in RdQuickStart.js.
 * Kept local here so DebugStrategies is self-contained.
 */
const RD_STRATEGIES = [
  {
    id: 'rd-odl',
    kind: 'rd',
    collector: 'ODL',
    icon: '📋',
    label: 'Start ODL Collection',
    desc: 'Trigger-based ODL log collection using the default log mask (50 MB max).',
  },
  {
    id: 'rd-minidump',
    kind: 'rd',
    collector: 'MINIDUMP',
    icon: '💥',
    label: 'Start Minidump',
    desc: 'Capture a minidump on crash — uses the existing generated Minidump configuration.',
  },
  {
    id: 'rd-microdump',
    kind: 'rd',
    collector: 'MICRODUMP',
    icon: '🗜️',
    label: 'Start Microdump',
    desc: 'Lightweight microdump crash capture — uses the existing generated Microdump configuration.',
  },
];

// Combined 5 strategies: 3 RD + 2 RT, ordered for the single grid row.
const ALL_STRATEGIES = [
  ...RD_STRATEGIES.map((s) => ({ ...s, type: 'rd' })),
  ...RT_STRATEGIES.map((s) => ({ ...s, type: 'rt' })),
];

const IDLE = { phase: 'idle', message: '' };

/**
 * DebugStrategies — 5-card coordinator for the DeviceProfile sub-tab.
 *
 * Shows 3 RD collector cards + 2 RT monitoring cards in a single grid row.
 * Active-session enforcement (hasActiveRd/hasActiveRt) is lifted from DeviceProfile
 * so the same hook instance drives both the enforcement here and the
 * ActiveDebugSessions list rendered by DeviceProfile.
 *
 * Props:
 *   systemId           {string}   — device system ID; cards disabled if empty
 *   userInfo           {string}   — logged-in user's email
 *   hasActiveRd        {boolean}  — true when the device already has an active RD session
 *   hasActiveRt        {boolean}  — true when the device already has an active RT session
 *   refresh            {function} — re-fetch sessions (call after successful start)
 *   setNdStartDevice   {function} — App.js setter to cross-navigate to New Diagnostics
 *   setSelectedTab     {function} — App.js setter to switch the top-level tab
 */
function DebugStrategies({ systemId, userInfo, hasActiveRd = false, hasActiveRt = false, refresh, setNdStartDevice, setSelectedTab }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(ALL_STRATEGIES.map((s) => [s.id, IDLE]))
  );
  const [errorMsg, setErrorMsg] = useState('');

  const setStatus = (id, patch) =>
    setStatuses((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const hasSystemId = Boolean(systemId && systemId.trim());

  // Keep service user context in sync
  useEffect(() => {
    if (userInfo) realTimeDeviceService.setUserContext(userInfo, null);
  }, [userInfo]);

  const handleCardClick = (id) => {
    if (!hasSystemId) return;
    const strategy = ALL_STRATEGIES.find((s) => s.id === id);
    if (!strategy) return;
    const blocked = strategy.type === 'rd' ? hasActiveRd : hasActiveRt;
    if (blocked) return;
    const s = statuses[id];
    if (s.phase === 'loading') return;
    if (s.phase === 'idle') setConfirmingId(id);
  };

  const handleCancel = () => setConfirmingId(null);

  const handleConfirm = async () => {
    const strategy = ALL_STRATEGIES.find((s) => s.id === confirmingId);
    setConfirmingId(null);
    if (!strategy) return;
    setStatus(strategy.id, { phase: 'loading', message: '' });
    setErrorMsg('');

    if (strategy.type === 'rd') {
      // ── RD path ──
      const result = await postData('/api/v1/quick-start-remote-diagnostics', {
        systemId: systemId.trim(),
        user: userInfo,
        collector: strategy.collector,
      });
      setStatus(strategy.id, IDLE);
      if (result && !result.errorMessage) {
        if (refresh) refresh();
      } else {
        setErrorMsg(result?.errorMessage || 'Unexpected error — please try again.');
      }
    } else {
      // ── RT path ──
      try {
        await realTimeDeviceService.startSession({
          systemId: systemId.trim(),
          durationMins: strategy.durationMins,
          scheduled: false,
          actions: strategy.actions,
        });
        setStatus(strategy.id, IDLE);
        if (refresh) refresh();
      } catch (err) {
        setStatus(strategy.id, IDLE);
        setErrorMsg(err?.message || 'Failed to start RT session — please try again.');
      }
    }
  };

  const confirmingStrategy = confirmingId ? ALL_STRATEGIES.find((s) => s.id === confirmingId) : null;

  return (
    <div className="ds-root">
      {/* ── 5-card grid ── */}
      <div className="ds-grid">
        {ALL_STRATEGIES.map(({ id, type, icon, label, desc }) => {
          const s = statuses[id];
          const isLoading = s.phase === 'loading';
          const blocked = type === 'rd' ? hasActiveRd : hasActiveRt;
          const isDisabled = !hasSystemId || isLoading || blocked;

          return (
            <div
              key={id}
              className={`rdqs-card${isLoading ? ' rdqs-card--loading' : ''}${isDisabled ? ' rdqs-card--disabled' : ''}`}
              onClick={() => !isDisabled && handleCardClick(id)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => e.key === 'Enter' && !isDisabled && handleCardClick(id)}
            >
              <div className="rdqs-icon">{icon}</div>
              {type === 'rd'
                ? <span className="rdqs-kind">Remote Diagnostics</span>
                : <span className="rdqs-kind rtqs-kind--rt">Real-Time</span>
              }
              <div className="rdqs-label">{label}</div>
              <div className="rdqs-desc">{desc}</div>

              {isLoading && (
                <div className="rdqs-status rdqs-status--loading">⏳ Starting…</div>
              )}
            </div>
          );
        })}
      </div>

      {!hasSystemId && (
        <p className="rdqs-no-device-hint">Run Investigate above to unlock these shortcuts.</p>
      )}

      {/* Blocked notices */}
      {hasSystemId && hasActiveRd && (
        <p className="rdqs-no-device-hint" style={{ color: '#92400e' }}>
          ⚠️ An RD session is already running on this device. Only one RD session can run per device — manage it in Active Debug Sessions below.
        </p>
      )}
      {hasSystemId && hasActiveRt && (
        <p className="rdqs-no-device-hint" style={{ color: '#92400e' }}>
          ⚠️ An RT session is already running on this device. Only one RT session can run per device — manage it in Active Debug Sessions below.
        </p>
      )}

      {/* Error notice (outside card) */}
      {errorMsg && (
        <p className="rdqs-no-device-hint" style={{ color: '#dc2626' }}>
          ⚠️ {errorMsg}
        </p>
      )}

      {/* ── Custom config links ── */}
      {hasSystemId && setNdStartDevice && setSelectedTab && (
        <div className="ds-config-links">
          <button
            className="rdqs-custom-link"
            onClick={() => { setNdStartDevice({ systemId: systemId.trim(), target: 'rd' }); setSelectedTab('New Diagnostics'); }}
          >
            ⚙️ Configure RD →
          </button>
          <button
            className="rdqs-custom-link"
            onClick={() => { setNdStartDevice({ systemId: systemId.trim(), target: 'rt' }); setSelectedTab('New Diagnostics'); }}
          >
            📡 Configure RT →
          </button>
        </div>
      )}

      {/* ── Confirm modal ── */}
      {confirmingStrategy && (
        <div className="rdqs-modal-backdrop" onClick={handleCancel}>
          <div className="rdqs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rdqs-modal-header">
              <h3 className="rdqs-modal-title">
                {confirmingStrategy.icon} {confirmingStrategy.label}
              </h3>
              <button className="rdqs-modal-close" onClick={handleCancel} aria-label="Close">✕</button>
            </div>
            <div className="rdqs-modal-body">
              <div className="rdqs-modal-field">
                <span className="rdqs-modal-field-label">Device System ID</span>
                <span className="rdqs-modal-sysid-pill">{systemId}</span>
              </div>

              {confirmingStrategy.type === 'rd' ? (
                // RD preset table — no Command row
                <div className="rdqs-preset-grid">
                  <div className="rdqs-preset-row">
                    <span className="rdqs-preset-key">Duration</span>
                    <span className="rdqs-preset-val">30 minutes</span>
                  </div>
                  <div className="rdqs-preset-row">
                    <span className="rdqs-preset-key">Mode</span>
                    <span className="rdqs-preset-val">Immediate start</span>
                  </div>
                  <div className="rdqs-preset-row">
                    <span className="rdqs-preset-key">Upload frequency</span>
                    <span className="rdqs-preset-val">Every 300 seconds</span>
                  </div>
                </div>
              ) : (
                // RT preset table
                <div className="rdqs-preset-grid">
                  <div className="rdqs-preset-row">
                    <span className="rdqs-preset-key">Duration</span>
                    <span className="rdqs-preset-val">{confirmingStrategy.durationMins} minutes</span>
                  </div>
                  <div className="rdqs-preset-row">
                    <span className="rdqs-preset-key">Mode</span>
                    <span className="rdqs-preset-val">Immediate start</span>
                  </div>
                  {confirmingStrategy.actions.map((a, i) => (
                    <div key={i} className="rdqs-preset-row">
                      <span className="rdqs-preset-key">
                        {confirmingStrategy.actions.length > 1 ? `Monitor ${i + 1}` : 'Monitor'}
                      </span>
                      <span className="rdqs-preset-val">
                        {a.metric.toUpperCase()} · every {a.interval}
                        {a.maxIterations ? ` · max ${a.maxIterations}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rdqs-modal-footer">
              <button className="rdqs-btn-go" onClick={handleConfirm}>▶ Start Session</button>
              <button className="rdqs-btn-cancel" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DebugStrategies;
